import type { MutableRefObject } from "react";
import { getLoggerRef } from "../../integrations/logger/logger-bridge";
import { getNoiseReductionType, registerAudioDeviceMonitor, NoiseReductionType } from "./noiseReduction";
import { createRealtimeMessageHandler } from "./realtimeEventHandler";

export interface PraxisCallSessionDeps {
  currentStream: MutableRefObject<any>;
  currentConnection: MutableRefObject<any>;
  dataChannel: MutableRefObject<any>;
  dcOpenTimeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  // Lets stopSession cancel the handler's internal grace-period timer. Must be a ref,
  // not a local var — createPraxisCallSession re-runs every render.
  handlerCleanupRef: MutableRefObject<(() => void) | null>;
  noiseReductionTypeRef: MutableRefObject<NoiseReductionType>;
  deviceMonitorCleanupRef: MutableRefObject<(() => void) | null>;
  audioContextRef: MutableRefObject<any>;
  // Ref — registerAudioDeviceMonitor's onUpdate callback is long-lived (registered
  // once per call), so it needs the fresh callId rather than a snapshot.
  callDetailsRef: MutableRefObject<any>;
  incomingMediaRecorderRef: MutableRefObject<any>;
  incomingAudioChunks: MutableRefObject<any[]>;
  outgoingMediaRecorderRef: MutableRefObject<any>;
  outgoingAudioChunks: MutableRefObject<any[]>;
  contextRequestGenerationRef: MutableRefObject<number>;
  isWaitingForModelResponseRef: MutableRefObject<boolean>;
  isUserCurrentlySpeakingRef: MutableRefObject<boolean>;
  pendingAssistantTranscriptRef: MutableRefObject<string>;
  lastCompletedAssistantMessageRef: MutableRefObject<string>;
  baseInstructionsRef: MutableRefObject<string>;
  transcriptRef: MutableRefObject<{ role: string; content: string }[]>;
  sentenceCacheRef: MutableRefObject<string[]>;
  timeLeftRef: MutableRefObject<number>;
  isCountdownCompleteRef: MutableRefObject<boolean>;
  setIsUploading: (value: boolean) => void;
  setIsConnected: (value: boolean) => void;
  setVoiceIntensity: (value: number) => void;
  setLoading: (value: boolean) => void;
  setDataSavingProgress: (value: number) => void;
  // Plain snapshots — same fresh-per-render/frozen-at-init-time semantics as the
  // original inline functions (see recording.ts / realtimeEventHandler.ts for the
  // same distinction).
  callDetails: any;
  onDisconnect: any;
  clearCallStateOnError: () => void;
  recording: {
    startRecording: () => Promise<void>;
    stopRecording: (endReason?: string) => Promise<void>;
    getAudioRecordings: () => { user_audio: any; assistant_audio: any };
  };
}

/** The WebRTC connection lifecycle: connect, negotiate, and tear down one call session. */
export function createPraxisCallSession(deps: PraxisCallSessionDeps) {
  const {
    currentStream,
    currentConnection,
    dataChannel,
    dcOpenTimeoutRef,
    handlerCleanupRef,
    noiseReductionTypeRef,
    deviceMonitorCleanupRef,
    audioContextRef,
    callDetailsRef,
    incomingMediaRecorderRef,
    incomingAudioChunks,
    outgoingMediaRecorderRef,
    outgoingAudioChunks,
    contextRequestGenerationRef,
    isWaitingForModelResponseRef,
    isUserCurrentlySpeakingRef,
    pendingAssistantTranscriptRef,
    lastCompletedAssistantMessageRef,
    baseInstructionsRef,
    transcriptRef,
    sentenceCacheRef,
    timeLeftRef,
    isCountdownCompleteRef,
    setIsUploading,
    setIsConnected,
    setVoiceIntensity,
    setLoading,
    setDataSavingProgress,
    callDetails,
    onDisconnect,
    clearCallStateOnError,
    recording,
  } = deps;
  const { startRecording, stopRecording, getAudioRecordings } = recording;

  const destroyStream = (stream: MediaStream) => {
    stream.getTracks().forEach((track) => {
      track.stop();
      track.enabled = false;
      stream.removeTrack(track);
    });
  };

  const handleClose = async (endReason?: string) => {
    if (callDetails?.isVideoRecording) {
      await stopRecording(endReason);
    }
    try {
      // @ts-ignore
      if (Array.isArray(window.stream)) {
        // @ts-ignore
        window.stream.forEach((stream) => {
          try {
            destroyStream(stream);
          } catch (e) {
            getLoggerRef()?.warn("Error destroying stream track", { error: String(e) });
            console.warn("Error destroying stream track:", e);
          }
        });
      }
    } finally {
      // @ts-ignore
      if (window.stream) window.stream = [];
    }
    // if not video recording, disconnect from call.
    if (!callDetails?.isVideoRecording) {
      try {
        setLoading(true);
        const { user_audio, assistant_audio } = getAudioRecordings();
        setDataSavingProgress(70);
        await onDisconnect({ user_audio, assistant_audio, endReason });
        if (!endReason) setDataSavingProgress(100);
      } catch (err) {
        getLoggerRef()?.error("Error in handleClose (non-video)", err as Error, { callId: callDetails?.id });
        console.error("Error in handleClose (non-video):", err);
        clearCallStateOnError();
      }
    }
  };

  // Function to monitor voice intensity
  function monitorVoiceIntensity(remoteStream) {
    if (audioContextRef.current) {
      audioContextRef.current.suspend?.();
      audioContextRef.current = null;
    }
    const audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    audioContextRef.current = audioContext;
    const source = audioContext.createMediaStreamSource(remoteStream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;

    source.connect(analyser);

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    function calculateIntensity() {
      analyser.getByteTimeDomainData(dataArray);

      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const normalizedValue = (dataArray[i] - 128) / 128; // Normalize to [-1, 1]
        sum += normalizedValue ** 2;
      }
      const rms = Math.sqrt(sum / dataArray.length);
      return rms;
    }

    function monitor() {
      const intensity = calculateIntensity();
      setVoiceIntensity(intensity);
      requestAnimationFrame(monitor);
    }

    monitor();
  }

  async function init({
    clientSecret,
    isVideoRecording,
  }: {
    clientSecret: string;
    isVideoRecording?: boolean;
  }) {
    if (currentConnection.current) {
      return;
    }
    if (isVideoRecording) {
      startRecording();
    }
    const EPHEMERAL_KEY = clientSecret;

    // Create a peer connection
    const pc = new RTCPeerConnection();
    currentConnection.current = pc;
    getLoggerRef()?.warn('WebRTC peer connection created', { callId: callDetails?.id });

    pc.onconnectionstatechange = () => {
      const level = (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') ? 'error' : 'info';
      if (level === 'error') {
        getLoggerRef()?.error('WebRTC connection state changed', undefined, {
          callId: callDetails?.id,
          connectionState: pc.connectionState,
          iceConnectionState: pc.iceConnectionState,
        });
      } else {
        getLoggerRef()?.warn('WebRTC connection state changed', {
          callId: callDetails?.id,
          connectionState: pc.connectionState,
          iceConnectionState: pc.iceConnectionState,
        });
      }
    };

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
        getLoggerRef()?.error('WebRTC ICE connection failed', undefined, {
          callId: callDetails?.id,
          iceConnectionState: pc.iceConnectionState,
        });
      }
    };
    // Set up to play remote audio from the model
    const audioEl = document.createElement("audio");
    audioEl.autoplay = true;
    pc.ontrack = (e) => {
      const remoteStream = e.streams[0];
      audioEl.srcObject = remoteStream;

      // Set up MediaRecorder just for logging
      const mediaRecorder = new MediaRecorder(e.streams[0], {
        mimeType: "audio/webm;codecs=opus",
      });
      incomingMediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          incomingAudioChunks.current.push(event.data);
        }
      };

      // Request data every 1 second
      mediaRecorder.start(1000);

      // Calculate voice intensity from the remote stream
      monitorVoiceIntensity(remoteStream);
    };

    // Add local audio track for microphone input in the browser.
    // If a permission-only request elsewhere is still in flight, the first getUserMedia can fail with "device busy"; retry once after a short delay.
    const constraints = { audio: { echoCancellation: true, noiseSuppression: true } };
    let ms: MediaStream;
    try {
      ms = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err: any) {
      const isDeviceBusy =
        err?.name === "NotReadableError" ||
        (typeof err?.message === "string" && /busy|in use/i.test(err.message));
      if (isDeviceBusy) {
        await new Promise((r) => setTimeout(r, 1500));
        ms = await navigator.mediaDevices.getUserMedia(constraints);
      } else {
        throw err;
      }
    }
    currentStream.current = ms;
    getLoggerRef()?.warn('Microphone acquired', { callId: callDetails?.id });

    noiseReductionTypeRef.current = getNoiseReductionType(ms);
    getLoggerRef()?.info('Noise reduction type detected', { type: noiseReductionTypeRef.current, deviceLabel: ms.getTracks()[0]?.label, callId: callDetails?.id });

    deviceMonitorCleanupRef.current = registerAudioDeviceMonitor(
      ms.getTracks()[0],
      () => dataChannel.current,
      () => noiseReductionTypeRef.current,
      (type) => { noiseReductionTypeRef.current = type; },
      (type) => getLoggerRef()?.info('Noise reduction updated on device change', { type, callId: callDetailsRef.current?.id }),
    );

    pc.addTrack(ms.getTracks()[0]);
    // add fake video track
    const canvas = document.createElement("canvas");
    const stream = canvas.captureStream();
    const blankVideoTrack = stream.getVideoTracks()[0];
    pc.addTrack(blankVideoTrack);

    const outgoingMediaRecorder = new MediaRecorder(ms, {
      mimeType: "audio/webm;codecs=opus",
    });
    outgoingMediaRecorderRef.current = outgoingMediaRecorder;
    outgoingMediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        if (!outgoingAudioChunks.current) {
          outgoingAudioChunks.current = [];
        }
        outgoingAudioChunks.current.push(event.data);
      }
    };
    outgoingMediaRecorder.start(1000);

    // Set up data channel for sending and receiving events
    const dc = pc.createDataChannel("oai-events");
    dataChannel.current = dc;

    dcOpenTimeoutRef.current = setTimeout(() => {
      dcOpenTimeoutRef.current = null;
      if (dc.readyState !== 'open') {
        getLoggerRef()?.error('OpenAI data channel failed to open within timeout', undefined, {
          callId: callDetails?.id,
          dataChannelState: dc.readyState,
          iceConnectionState: pc.iceConnectionState,
          connectionState: pc.connectionState,
        });
      }
    }, 15000);

    dc.addEventListener('open', () => {
      if (dcOpenTimeoutRef.current) {
        clearTimeout(dcOpenTimeoutRef.current);
        dcOpenTimeoutRef.current = null;
      }
      getLoggerRef()?.warn('OpenAI data channel opened', { callId: callDetails?.id });
    });

    dc.addEventListener('error', (e: Event) => {
      const err = (e as RTCErrorEvent).error;
      getLoggerRef()?.error('OpenAI data channel error', err ?? undefined, {
        callId: callDetails?.id,
      });
    });

    const realtimeMessageHandlerDeps = {
      dataChannel,
      contextRequestGenerationRef,
      isWaitingForModelResponseRef,
      isUserCurrentlySpeakingRef,
      pendingAssistantTranscriptRef,
      lastCompletedAssistantMessageRef,
      baseInstructionsRef,
      noiseReductionTypeRef,
      transcriptRef,
      sentenceCacheRef,
      timeLeftRef,
      isCountdownCompleteRef,
      setIsUploading,
      setIsConnected,
      callDetails,
    };
    const handler = createRealtimeMessageHandler(realtimeMessageHandlerDeps);
    handlerCleanupRef.current = handler.cleanup;
    dc.addEventListener("message", handler.handleMessage);

    // Start the session using the Session Description Protocol (SDP)
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const url = "https://api.openai.com/v1/realtime/calls";
    getLoggerRef()?.addBreadcrumb('api', 'POST api.openai.com/v1/realtime/calls (SDP negotiation)', { callId: callDetails?.id });
    const sdpResponse = await fetch(url, {
      method: "POST",
      body: offer.sdp,
      headers: {
        Authorization: `Bearer ${EPHEMERAL_KEY}`,
        "Content-Type": "application/sdp",
      },
    });

    if (!sdpResponse.ok) {
      getLoggerRef()?.error("OpenAI SDP negotiation failed", undefined, {
        status: sdpResponse.status,
        callId: callDetails?.id,
        source: 'openai-realtime',
      });
      throw new Error(`OpenAI SDP negotiation failed with status ${sdpResponse.status}`);
    }

    const answer: any = {
      type: "answer",
      sdp: await sdpResponse.text(),
    };
    await pc.setRemoteDescription(answer);
    getLoggerRef()?.warn('SDP negotiation complete, awaiting session.created', { callId: callDetails?.id });
  }

  async function stopSession(endReason?: string) {
    getLoggerRef()?.info('Session stop initiated', { sessionId: callDetails?.id, endReason });

    handlerCleanupRef.current?.();
    handlerCleanupRef.current = null;

    deviceMonitorCleanupRef.current?.();
    deviceMonitorCleanupRef.current = null;

    if (currentStream.current) {
      currentStream.current.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      currentStream.current = null;
    }

    if (currentConnection.current) {
      currentConnection.current.close();
      currentConnection.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current?.suspend?.();
      audioContextRef.current = null;
    }

    if (dcOpenTimeoutRef.current) {
      clearTimeout(dcOpenTimeoutRef.current);
      dcOpenTimeoutRef.current = null;
    }

    if (dataChannel.current) {
      dataChannel.current.close();
      dataChannel.current = null;
    }

    await handleClose(endReason);
  }

  async function startSession({ clientSecret }: { clientSecret: string }) {
    const opLogger = getLoggerRef()?.withOperation('start-praxis-call');
    getLoggerRef()?.warn('PraxisPlay startSession called', { callId: callDetails?.id, isVideoRecording: callDetails?.isVideoRecording });
    init({ clientSecret, isVideoRecording: callDetails?.isVideoRecording })
      .then(() => { })
      .catch((error) => {
        opLogger?.error("Session init error", error as Error, { callId: callDetails?.id });
        console.error("Session error:", error, error.message);
        void stopSession().catch((e) => {
          opLogger?.error("stopSession on init failure", e as Error, { callId: callDetails?.id });
          console.error("stopSession on init failure:", e);
        });
      });
  }

  return { startSession, stopSession };
}
